import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  AutomationActionStatus,
  IExperience,
  MessageStatus,
  SubTaskStatus
} from '@dadohr/shared/domainTypes/experience';

/**
 * Derive a preSkips array from a manual-enroll staged instance.
 *
 * The manual-enroll dialog stages admin "Skip" actions by mutating the
 * local instance preview — `skipTask` flips `task.status` + every OPEN
 * subtask to SKIPPED; `skipMessage` flips `messagesStatuses[id].status`
 * to SKIPPED; `skipAutomationAction` flips `automation.actions[id].status`
 * to SKIPPED.
 *
 * In v1, `enrollUsers` persisted the staged instance directly via
 * `batchSetDocs`, so those mutations landed in Firestore as-is. In v2,
 * `enrollUsers` routes through the `experiences_enrollParticipant`
 * callable, which rebuilds the instance from the TEMPLATE at enrollment
 * time — the local mutations would be discarded. We derive an equivalent
 * `preSkips` array here so the BE builder
 * (`functions/experiences/src/public/index.ts:createExperienceInstance`)
 * lands the same SKIPPED states.
 *
 * v1 reference: pkgs/cf/functions/src/modules/experiences/service.ts:888
 * / 1003 / 1379 / 1548 / 1653 reads preSkips off the instance via the
 * same `{ type, id }` contract; v1's `app/src/services/experiences/template.ts:enrollUsers`
 * persisted the staged instance via `batchSetDocs`. We keep behavior in
 * parity by lifting the same SKIPPED detection into the v2 enroll path.
 *
 * Bead: new-main-q2k6.
 *
 * Note: ids in preSkips are TEMPLATE ids — `subTask.templateId`,
 * `messagesStatuses` key (which IS the template id), and
 * `action.templateId`. The BE builder generates fresh instance ids at
 * enrollment, so matching on template ids is the only stable contract.
 */
export const derivePreSkipsFromStagedInstance = (
  d: Doc<IExperience>
): { type: 'SUB_TASK' | 'MSG' | 'AUTOMATION_ACTION'; id: string }[] => {
  const out: {
    type: 'SUB_TASK' | 'MSG' | 'AUTOMATION_ACTION';
    id: string;
  }[] = [];

  d.data.tasks?.forEach((t) => {
    t.subTasks?.forEach((s) => {
      if (s.status === SubTaskStatus.SKIPPED && s.templateId) {
        out.push({ type: 'SUB_TASK', id: s.templateId });
      }
    });
  });

  Object.entries(d.data.messagesStatuses || {}).forEach(([msgId, ms]) => {
    if (ms.status === MessageStatus.SKIPPED) {
      out.push({ type: 'MSG', id: msgId });
    }
  });

  Object.values(d.data.automations || {}).forEach((aut) => {
    Object.values(aut.actions || {}).forEach((ac) => {
      if (ac.status === AutomationActionStatus.SKIPPED && ac.templateId) {
        out.push({ type: 'AUTOMATION_ACTION', id: ac.templateId });
      }
    });
  });

  return out;
};
